import React, { useState, useEffect, useMemo, useRef } from 'react';
import { COLOR_RAMPS, sampleRamp, numericValues } from '../lib/classify';
import {
  MODES, BREAK_METHODS, POINT_SYMBOLS, DEFAULT_STYLE, buildClassPreview, finalizeStyle,
} from '../lib/layer-style';
import { run, isAbortError, CLASSIFY_WORKER_THRESHOLD } from '../lib/worker-pool';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Per-layer symbology with classification (G42), split into reusable parts:
 *   useStyleThemeColors()  - the theme colours plus the accent colours the controls use
 *   fieldStyles(C)         - the shared select / label styles
 *   useStylePreview()      - attribute info + class preview (worker-pool classification above
 *                            CLASSIFY_WORKER_THRESHOLD values)
 *   StyleFields            - the controls themselves (render mode, ramp, classes, colours,
 *                            symbol, sliders); composed by the Style tab of
 *                            components/properties/PropertiesPanel.js
 * and the default export, the standalone editor (header + fields + Apply / Reset footer).
 */

export function useStyleThemeColors() {
  const { C: themeC } = useTheme();
  return { ...themeC, amber: themeC.warnText };
}

export function fieldStyles(C) {
  return {
    sel: { width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' },
    lbl: { fontSize: 10, color: C.text2, display: 'block', marginBottom: 2 },
  };
}

/** Attribute keys of a layer's features with a numeric flag (first 500 features). */
export function attributeInfo(features) {
  const keys = {};
  features.slice(0, 500).forEach(f => {
    Object.entries(f.properties || {}).forEach(([k, v]) => {
      if (v == null || v === '') return;
      if (!keys[k]) keys[k] = { numeric: 0, total: 0 };
      keys[k].total++;
      if (typeof v === 'number' || (!isNaN(parseFloat(v)) && isFinite(v))) keys[k].numeric++;
    });
  });
  return Object.entries(keys).map(([k, info]) => ({ key: k, isNumeric: info.numeric / info.total > 0.8 }));
}

/**
 * Attribute info and the live class preview for a draft style. Above
 * CLASSIFY_WORKER_THRESHOLD values the graduated breaks are computed in the worker pool
 * (lib/worker-pool.js); smaller inputs stay synchronous.
 * Returns { attrInfo, numericAttrs, preview, classifying, classifyError, valueCount }.
 */
export function useStylePreview(style, features) {
  const attrInfo = useMemo(() => attributeInfo(features), [features]);
  const numericAttrs = attrInfo.filter(a => a.isNumeric);

  const gradVals = useMemo(
    () => (style.mode === 'graduated' && style.attr ? numericValues(features, style.attr) : null),
    [style.mode, style.attr, features],
  );
  const heavy = !!gradVals && gradVals.length > CLASSIFY_WORKER_THRESHOLD;
  const [asyncBreaks, setAsyncBreaks] = useState(null); // { vals, nClasses, method, breaks }
  const [classifyError, setClassifyError] = useState(null);

  useEffect(() => {
    if (!heavy) return undefined;
    let active = true;
    const ctl = new AbortController();
    setClassifyError(null);
    const values = Float64Array.from(gradVals);
    run('classify', { values, nClasses: style.nClasses, method: style.method },
      { label: `Classify ${style.attr} (${gradVals.length.toLocaleString()} values)`, signal: ctl.signal, transfer: [values.buffer] })
      .then((breaks) => {
        if (active) setAsyncBreaks({ vals: gradVals, nClasses: style.nClasses, method: style.method, breaks });
      })
      .catch((err) => {
        if (active && !isAbortError(err)) setClassifyError(err.message);
      });
    return () => { active = false; ctl.abort(); };
  }, [heavy, gradVals, style.nClasses, style.method, style.attr]);

  const asyncReady = heavy && asyncBreaks && asyncBreaks.vals === gradVals
    && asyncBreaks.nClasses === style.nClasses && asyncBreaks.method === style.method;
  const classifying = heavy && !asyncReady && !classifyError;

  // Only the classification inputs: sliders / colours must not re-run the classification.
  const { mode, attr, nClasses, method, ramp, reversed } = style;
  const preview = useMemo(() => {
    const cs = { mode, attr, nClasses, method, ramp, reversed };
    if (heavy && mode === 'graduated') {
      if (!asyncReady) return null; // still classifying in the worker
      return buildClassPreview(cs, features, asyncBreaks.breaks);
    }
    return buildClassPreview(cs, features);
  }, [mode, attr, nClasses, method, ramp, reversed, features, heavy, asyncReady, asyncBreaks]);

  return { attrInfo, numericAttrs, preview, classifying, classifyError, valueCount: gradVals ? gradVals.length : 0 };
}

const colorInput = (C) => ({ width: '100%', height: 28, padding: 0, border: `1px solid ${C.border}`, borderRadius: 6, background: C.bg4, cursor: 'pointer' });

/**
 * The symbology controls. `info` is the result of useStylePreview; `set(patch)` updates the
 * draft style. `geomType` ('point' | 'line' | 'polygon' | null) hides controls that do not
 * apply (heatmap and point symbols are point-only, fill colour does not apply to lines).
 */
export function StyleFields({ style, set, info, geomType = null }) {
  const C = useStyleThemeColors();
  const { sel: selStyle, lbl } = fieldStyles(C);
  const { attrInfo, numericAttrs, preview, classifying, classifyError, valueCount } = info;
  const isPoint = !geomType || geomType === 'point';
  const hasFill = geomType !== 'line';
  const modes = MODES.filter(m => m.id !== 'heatmap' || isPoint);
  const heat = style.mode === 'heatmap';
  const classified = style.mode === 'categorized' || style.mode === 'graduated';

  return (
    <>
      <div>
        <label style={lbl}>Symbology</label>
        <div style={{ display: 'flex', gap: 4 }} role="group" aria-label="Render mode">
          {modes.map(m => (
            <button key={m.id} onClick={() => set({ mode: m.id })} aria-pressed={style.mode === m.id}
              style={{ flex: 1, fontSize: 10, padding: '5px 2px', borderRadius: 6, cursor: 'pointer', background: style.mode === m.id ? C.bg4 : 'transparent', color: style.mode === m.id ? C.accent : C.text2, border: `1px solid ${style.mode === m.id ? C.accentBorder : C.border}` }}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {classified && (
        <>
          <div>
            <label style={lbl} htmlFor="ugis-style-attr">Attribute {style.mode === 'graduated' ? '(numeric)' : ''}</label>
            <select id="ugis-style-attr" value={style.attr} onChange={e => set({ attr: e.target.value })} style={selStyle}>
              <option value="">Select attribute...</option>
              {(style.mode === 'graduated' ? numericAttrs : attrInfo).map(a => (
                <option key={a.key} value={a.key}>{a.key}{a.isNumeric ? ' (#)' : ''}</option>
              ))}
            </select>
          </div>

          {style.mode === 'graduated' && (
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl} htmlFor="ugis-style-method">Method</label>
                <select id="ugis-style-method" value={style.method} onChange={e => set({ method: e.target.value })} style={selStyle}>
                  {BREAK_METHODS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>
              <div style={{ width: 62 }}>
                <label style={lbl} htmlFor="ugis-style-classes">Classes</label>
                <input id="ugis-style-classes" type="number" min={2} max={9} value={style.nClasses}
                  onChange={e => set({ nClasses: Math.max(2, Math.min(9, parseInt(e.target.value) || 5)) })}
                  style={{ ...selStyle, boxSizing: 'border-box' }} />
              </div>
            </div>
          )}
        </>
      )}

      {(classified || heat) && (
        <div>
          <label style={lbl} htmlFor="ugis-style-ramp">Color Ramp</label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <select id="ugis-style-ramp" value={style.ramp} onChange={e => set({ ramp: e.target.value })} style={selStyle}>
              {Object.entries(COLOR_RAMPS).map(([key, r]) => (
                <option key={key} value={key}>{r.label} ({r.type})</option>
              ))}
            </select>
            <label style={{ fontSize: 10, color: C.text2, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 3 }}>
              <input type="checkbox" checked={!!style.reversed} onChange={e => set({ reversed: e.target.checked })} />
              Reverse
            </label>
          </div>
          <div style={{ display: 'flex', height: 10, borderRadius: 4, overflow: 'hidden', marginTop: 4 }} data-testid="ramp-preview">
            {(style.reversed ? [...sampleRamp(style.ramp, 12)].reverse() : sampleRamp(style.ramp, 12)).map((c, i) => <div key={i} style={{ flex: 1, background: c }} />)}
          </div>
        </div>
      )}

      {classifying && <div style={{ fontSize: 10, color: C.text2 }}>Classifying {valueCount.toLocaleString()} values in background…</div>}
      {classifyError && <div style={{ fontSize: 10, color: C.red }}>Classification failed: {classifyError}</div>}

      {classified && preview && (
        <div style={{ background: C.bg3, borderRadius: 8, border: `1px solid ${C.border}`, padding: 8, maxHeight: 150, overflow: 'auto' }}>
          <div style={{ fontSize: 9, color: C.text3, textTransform: 'uppercase', marginBottom: 4 }}>
            {preview.type === 'graduated' ? `${preview.breaks.length - 1} classes` : `${preview.categories.length} categories`}
          </div>
          {preview.type === 'graduated'
            ? preview.colors.map((c, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <div style={{ width: 14, height: 14, borderRadius: 3, background: c, flexShrink: 0 }} />
                <span style={{ fontSize: 10, color: C.text1 }}>
                  {preview.breaks[i]?.toFixed(2)} – {preview.breaks[i + 1]?.toFixed(2)}
                </span>
              </div>
            ))
            : preview.categories.map((cat, i) => (
              <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <div style={{ width: 14, height: 14, borderRadius: 3, background: preview.colors[i], flexShrink: 0 }} />
                <span style={{ fontSize: 10, color: C.text1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat}</span>
              </div>
            ))}
        </div>
      )}

      {style.mode === 'single' && (
        <div style={{ display: 'flex', gap: 8 }}>
          {hasFill && (
            <div style={{ flex: 1 }}>
              <label style={lbl} htmlFor="ugis-style-fill">{isPoint ? 'Fill / Point' : 'Fill'}</label>
              <input id="ugis-style-fill" type="color" value={style.fillColor} onChange={e => set({ fillColor: e.target.value })} style={colorInput(C)} />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <label style={lbl} htmlFor="ugis-style-stroke">{geomType === 'line' ? 'Line' : 'Outline'}</label>
            <input id="ugis-style-stroke" type="color" value={style.strokeColor} onChange={e => set({ strokeColor: e.target.value })} style={colorInput(C)} />
          </div>
        </div>
      )}

      {isPoint && !heat && (
        <div>
          <label style={lbl}>Point symbol</label>
          <div style={{ display: 'flex', gap: 4 }} role="group" aria-label="Point symbol">
            {POINT_SYMBOLS.map(s => (
              <button key={s.id} onClick={() => set({ symbol: s.id })} aria-pressed={(style.symbol || 'circle') === s.id}
                style={{ flex: 1, fontSize: 10, padding: '4px 2px', borderRadius: 6, cursor: 'pointer', background: (style.symbol || 'circle') === s.id ? C.bg4 : 'transparent', color: (style.symbol || 'circle') === s.id ? C.accent : C.text2, border: `1px solid ${(style.symbol || 'circle') === s.id ? C.accentBorder : C.border}` }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <label style={lbl} htmlFor="ugis-style-opacity">Opacity — {Math.round(style.opacity * 100)}%</label>
        <input id="ugis-style-opacity" type="range" min={0.1} max={1} step={0.05} value={style.opacity}
          onChange={e => set({ opacity: parseFloat(e.target.value) })}
          style={{ width: '100%', accentColor: C.accent }} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {isPoint && (
          <div style={{ flex: 1 }}>
            <label style={lbl} htmlFor="ugis-style-size">{heat ? 'Heat radius' : 'Point Size'} — {style.pointSize}{heat ? '' : 'px'}</label>
            <input id="ugis-style-size" type="range" min={1} max={16} step={0.5} value={style.pointSize}
              onChange={e => set({ pointSize: parseFloat(e.target.value) })}
              style={{ width: '100%', accentColor: C.accent }} />
          </div>
        )}
        {!heat && (
          <div style={{ flex: 1 }}>
            <label style={lbl} htmlFor="ugis-style-width">{geomType === 'line' ? 'Line Width' : 'Outline Width'} — {style.strokeWidth}px</label>
            <input id="ugis-style-width" type="range" min={0.5} max={8} step={0.5} value={style.strokeWidth}
              onChange={e => set({ strokeWidth: parseFloat(e.target.value) })}
              style={{ width: '100%', accentColor: C.accent }} />
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Standalone style editor (kept for embedding outside the dock; the map workspace uses
 * the properties panel, which composes the same StyleFields).
 *
 * Props:
 *   layers        - [{ id, name, geojson }]
 *   styles        - { [layerId]: styleObj } current overrides
 *   initialLayerId- layer preselected
 *   onApply(layerId, styleObj|null) — null resets to default style
 *   onClose()
 */
export default function StyleEditor({ layers = [], styles = {}, initialLayerId, onApply, onClose }) {
  const C = useStyleThemeColors();
  const { sel: selStyle, lbl } = fieldStyles(C);
  const [layerId, setLayerId] = useState(initialLayerId || layers[0]?.id || '');
  const [style, setStyle] = useState({ ...DEFAULT_STYLE, ...(styles[initialLayerId || layers[0]?.id] || {}) });

  const layer = layers.find(l => l.id === layerId);
  const features = useMemo(() => layer?.geojson?.features || [], [layer]);

  // Reload style state when switching layers (styles intentionally omitted from deps —
  // we only want to reset the form on layer switch, not on every apply)
  const stylesRef = useRef(styles);
  stylesRef.current = styles;
  useEffect(() => {
    setStyle({ ...DEFAULT_STYLE, ...(stylesRef.current[layerId] || {}) });
  }, [layerId]);

  const set = (patch) => setStyle(s => ({ ...s, ...patch }));
  const info = useStylePreview(style, features);
  const blocked = !layerId || (style.mode !== 'single' && style.mode !== 'heatmap' && !style.attr);

  const apply = () => {
    if (info.classifying) return; // breaks still computing in the worker
    onApply(layerId, finalizeStyle(style, info.preview));
  };

  return (
    <div style={{ width: 270, background: C.bg2, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>🎨 Style Editor</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 14 }}>✕</button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <label style={lbl}>Layer</label>
          <select value={layerId} onChange={e => setLayerId(e.target.value)} style={selStyle}>
            {layers.length === 0 && <option value="">No layers</option>}
            {layers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <StyleFields style={style} set={set} info={info} />
      </div>

      <div style={{ padding: 10, borderTop: `1px solid ${C.border}`, display: 'flex', gap: 6 }}>
        <button onClick={() => onApply(layerId, null)}
          style={{ flex: 1, padding: '7px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg4, color: C.text1, cursor: 'pointer', fontSize: 11 }}>
          Reset
        </button>
        <button onClick={apply} disabled={blocked}
          style={{ flex: 2, padding: '7px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#00b8de,#3b82f6)', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', opacity: blocked ? 0.5 : 1 }}>
          Apply Style
        </button>
      </div>
    </div>
  );
}
